// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/drift/DriftIntelligencePanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";
import "./DriftIntelligencePanel.css";

const DRIFT_PROMPTS = [
  "What needs attention now?",
  "What changed?",
  "What is driving current drift?",
  "What should I review first?",
];

function normalizeRows(value) {
  return Array.isArray(value) ? value : [];
}

function formatConfidence(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();
  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sourceLabel(source = {}) {
  const explicit = String(source?.label || source?.item_label || "").trim();
  if (explicit) return explicit;

  return String(source?.source || source?.id || source?.type || "Drift source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function sourceDetail(source = {}) {
  const represented = Number(source?.item_count);
  const records = Number(source?.records);

  if (Number.isFinite(represented) && represented >= 0) return `${represented} represented`;
  if (Number.isFinite(records) && records >= 0) return `${records} loaded`;
  return "Grounded";
}

function DriftSkeleton() {
  return (
    <div className="drift-ai-panel__skeleton" role="status" aria-label="Loading Drift Intelligence">
      <span />
      <span />
      <span />
    </div>
  );
}

export default function DriftIntelligencePanel({
  requestJson,
  framework,
  refreshKey = 0,
}) {
  const [card, setCard] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(DRIFT_PROMPTS[0]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSequenceRef = useRef(0);
  const mountedRef = useRef(true);

  const ask = useCallback(
    async (question) => {
      if (typeof requestJson !== "function") {
        setError("Drift Intelligence is not available because the API client is not configured.");
        return;
      }

      const sequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = sequence;
      setLoading(true);
      setError("");
      setActiveQuestion(question);

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          body: {
            scope: "drift",
            question,
            copilot_context: {
              page: "drift",
              surface: "drift_page",
              intelligence_mode: "page",
              framework,
            },
          },
        });

        if (!mountedRef.current || sequence !== requestSequenceRef.current) return;

        const nextCard = payload?.card && typeof payload.card === "object" ? payload.card : null;
        if (!nextCard) throw new Error("Drift Intelligence returned no grounded response card.");
        setCard(nextCard);
      } catch (err) {
        if (!mountedRef.current || sequence !== requestSequenceRef.current) return;
        console.error("Drift ambient intelligence error:", err);
        setError(String(err?.message || err || "Unable to load Drift Intelligence."));
      } finally {
        if (mountedRef.current && sequence === requestSequenceRef.current) {
          setLoading(false);
        }
      }
    },
    [framework, requestJson]
  );

  useEffect(() => {
    mountedRef.current = true;
    ask(DRIFT_PROMPTS[0]);

    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, [ask, refreshKey]);

  const recommendations = useMemo(
    () => normalizeRows(card?.recommended_actions).filter(Boolean).slice(0, 3),
    [card]
  );
  const evidence = useMemo(
    () => normalizeRows(card?.evidence_used).filter((item) => item && typeof item === "object").slice(0, 12),
    [card]
  );
  const limitations = useMemo(
    () => normalizeRows(card?.known_limitations).filter(Boolean),
    [card]
  );

  const metadata = card?.metadata && typeof card.metadata === "object" ? card.metadata : {};
  const auditPersisted = metadata?.audit_persisted === true;

  return (
    <section className="drift-ai-panel" aria-labelledby="drift-ai-panel-title">
      <div className="drift-ai-panel__header">
        <div className="drift-ai-panel__identity">
          <span className="drift-ai-panel__eyebrow">OASYS INTELLIGENCE</span>
          <h2 id="drift-ai-panel-title">Drift Intelligence</h2>
          <p>
            Grounded reasoning across current drift and state. Native drift intervals, control state,
            freshness, and linked operational records remain authoritative.
          </p>
        </div>

        <div className="drift-ai-panel__header-meta">
          <div className="drift-ai-panel__status oasys-theme-light">
            <StatusChip tone={confidenceTone(card?.confidence)} dot={Boolean(card)}>
              {loading && !card
                ? "Reasoning…"
                : card
                ? formatConfidence(card.confidence)
                : "Awaiting context"}
            </StatusChip>

            {auditPersisted ? <StatusChip tone="success">Audited</StatusChip> : null}

            <Button
              variant="ghost"
              size="small"
              onClick={() => setDetailsOpen((current) => !current)}
              aria-expanded={detailsOpen}
              aria-label={detailsOpen ? "Hide intelligence grounding details" : "Show intelligence grounding details"}
              className="drift-ai-panel__details-toggle"
            >
              {detailsOpen ? "Hide details" : "Details"}
            </Button>
          </div>

          <div className="drift-ai-panel__scope">
            <span>Framework</span>
            <strong>{framework || "Current"}</strong>
          </div>
        </div>
      </div>

      <div className="drift-ai-panel__prompts" role="group" aria-label="Drift Intelligence questions">
        {DRIFT_PROMPTS.map((prompt) => (
          <Button
            key={prompt}
            variant={activeQuestion === prompt ? "secondary" : "ghost"}
            size="small"
            disabled={loading}
            aria-pressed={activeQuestion === prompt}
            onClick={() => ask(prompt)}
            className="drift-ai-panel__prompt"
          >
            {prompt}
          </Button>
        ))}
      </div>

      <div className="drift-ai-panel__body" aria-live="polite" aria-busy={loading}>
        {!card && loading ? <DriftSkeleton /> : null}

        {error ? (
          <div className="drift-ai-panel__error" role="alert">
            <strong>Intelligence unavailable</strong>
            <span>{error}</span>
          </div>
        ) : null}

        {card ? (
          <>
            <div className="drift-ai-panel__reasoning">
              <span className="drift-ai-panel__label">Current reasoning</span>
              <strong>{card.summary || "Drift & State context loaded."}</strong>
              {card.reasoning ? <p>{card.reasoning}</p> : null}
            </div>

            {recommendations.length ? (
              <div className="drift-ai-panel__actions">
                <span className="drift-ai-panel__label">Recommended operator focus</span>
                <ol>
                  {recommendations.map((action, index) => (
                    <li key={`${String(action)}-${index}`}>
                      <span>{index + 1}</span>
                      <p>{String(action)}</p>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {detailsOpen ? (
        <div className="drift-ai-panel__details">
          <div>
            <span className="drift-ai-panel__label">Grounding</span>
            {evidence.length ? (
              <div className="drift-ai-panel__sources">
                {evidence.map((source, index) => (
                  <div
                    className="drift-ai-panel__source"
                    key={`${source?.source || source?.type || "source"}-${index}`}
                  >
                    <strong>{sourceLabel(source)}</strong>
                    <span>{sourceDetail(source)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="drift-ai-panel__muted">No source groups were returned for this response.</p>
            )}
          </div>

          <div>
            <span className="drift-ai-panel__label">Known limitations</span>
            {limitations.length ? (
              <ul className="drift-ai-panel__limitations">
                {limitations.map((limitation, index) => (
                  <li key={`${String(limitation)}-${index}`}>{String(limitation)}</li>
                ))}
              </ul>
            ) : (
              <p className="drift-ai-panel__muted">No additional limitations were reported.</p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
